/**
 * The one place the "which name do I show" decision lives.
 *
 * ── WHY A SHARED FUNCTION AND NOT FORTY COPIES ──────────────────────────────────────────────────
 *
 * Forty sites each carried the same three lines:
 *
 *   const useReal = classData.showRealNames && student.realFirstName;
 *   const first = useReal ? student.realFirstName! : student.firstName;
 *   const last  = useReal ? (student.realLastName || '') : student.lastName;
 *
 * Uniform, which is lucky, because adding a second source of a real name to forty copies by hand is how
 * one of them ends up showing a pseudonym in the gradebook and a real name in the export. The grid, the
 * seating chart, the leaderboard, the report comments and the CSV must agree, and a teacher who sees two
 * different names for one child has no way to tell which is right.
 *
 * ── TWO SOURCES, DURING AND AFTER THE TRANSITION ────────────────────────────────────────────────
 *
 * The vault is the new one. `realFirstName`/`realLastName` on the student is the OLD one — teacher-linked
 * names stored in `classes/{id}/private/roster`, i.e. readable by us, which is the whole reason the vault
 * exists. Both have to work: twelve nickname classes exist today and none has been migrated, so removing
 * the legacy read would blank names that teachers can currently see.
 *
 * The vault wins when it has an answer. A class holding both has been migrated and the roster copy is a
 * leftover awaiting deletion.
 *
 * ── WHAT THE TOGGLE MEANS ───────────────────────────────────────────────────────────────────────
 *
 * `showRealNames` governs both sources, unchanged. It is the teacher's own switch for projecting the class
 * on a screen, and it was never about where the name came from.
 */

export interface DisplayName {
  first: string;
  last: string;
  /** True when a REAL name is being shown. Callers use it to label, warn, or refuse to export. */
  isReal: boolean;
}

/** What a vault-backed resolver provides. Strings only — never a record that could be merged. */
export type NameResolver = (student: { id: string; firstName?: string; lastName?: string })
  => { firstName: string; lastName: string; isReal: boolean };

export interface DisplayNameOpts {
  /** From `useNameResolver`. Absent outside a VaultProvider, or in a class with no vault. */
  resolve?: NameResolver;
  /** The class's own show-real-names switch. */
  showRealNames?: boolean;
}

export interface NameableStudent {
  id?: string;
  firstName?: string;
  lastName?: string;
  /** LEGACY: the cloud-stored linked name. Still read, because unmigrated classes rely on it. */
  realFirstName?: string;
  realLastName?: string;
}

/**
 * The name to put on screen for this student.
 *
 * Falls back to the pseudonym in every uncertain case — no provider, no vault, locked, not vaulted, toggle
 * off. A pseudonym is always safe to show; a real name shown when it should not be is the failure this
 * whole feature exists to prevent, and a BLANK reads to a teacher as data loss.
 */
export function displayNameOf(student: NameableStudent, opts: DisplayNameOpts = {}): DisplayName {
  const pseudonym: DisplayName = {
    first: student.firstName ?? '',
    last: student.lastName ?? '',
    isReal: false,
  };
  if (!opts.showRealNames) return pseudonym;

  /**
   * The vault first. When a class has been migrated, the roster copy below is a leftover awaiting deletion
   * and may be stale — a teacher who corrected a spelling in the vault must see the correction.
   */
  if (opts.resolve && student.id) {
    const r = opts.resolve({ id: student.id, firstName: student.firstName, lastName: student.lastName });
    if (r.isReal) return { first: r.firstName, last: r.lastName, isReal: true };
  }

  // LEGACY. Still needed: twelve nickname classes exist and none has been migrated.
  if (student.realFirstName) {
    return { first: student.realFirstName, last: student.realLastName || '', isReal: true };
  }

  return pseudonym;
}

/** "Last, First", the roster convention. */
export const lastFirstOf = (n: DisplayName): string =>
  n.last ? `${n.last}, ${n.first}`.trim() : n.first;

/** "First Last". */
export const fullNameOf = (n: DisplayName): string => `${n.first} ${n.last}`.trim();

/** Initials, for a seat tile or a dense grid. */
export const initialsOf = (n: DisplayName): string =>
  `${(n.first[0] ?? '').toUpperCase()}${(n.last[0] ?? '').toUpperCase()}`;

/** "First L." — enough to identify a student in a narrow column without a full surname. */
export const abbreviatedOf = (n: DisplayName): string =>
  n.last ? `${n.first} ${n.last[0].toUpperCase()}.` : n.first;

/**
 * Sort by the name the teacher is actually LOOKING at.
 *
 * A comparator cannot call a hook, which is why the resolver is passed in. Sorting by the pseudonym while
 * displaying the real name puts the list in an order that looks random — Maya above Noah above Chloé — and
 * a teacher scanning for a student simply cannot find them.
 *
 * The resolver is optional so every existing caller keeps working unchanged; without it this behaves
 * exactly as before.
 */
export function sortByDisplayName<T extends NameableStudent>(
  students: readonly T[],
  opts: DisplayNameOpts = {},
): T[] {
  const key = (s: T) => {
    // Sorting always uses the real name when one is available, regardless of the projection toggle: the
    // ORDER should not change when a teacher hides names to show the class on a screen.
    const n = displayNameOf(s, { ...opts, showRealNames: true });
    return { last: n.last || '', first: n.first || '' };
  };
  return [...students].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    const byLast = ka.last.localeCompare(kb.last);
    return byLast !== 0 ? byLast : ka.first.localeCompare(kb.first);
  });
}
